// ─── PlaybookPanel.jsx ───────────────────────────────────────────────────────
// Automated playbook suggestions per attack type.
// ─────────────────────────────────────────────────────────────────────────────

import { apiFetch } from '../../utils/apiClient';
import React, { useState, useEffect, useCallback, useRef } from 'react';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

const PLAYBOOKS = {
  'APT-Reconnaissance': {
    title: 'Reconnaissance Detected',
    priority: 'MEDIUM',
    steps: [
      'Block source IP at perimeter firewall immediately',
      'Enable enhanced logging on all scanned services (ports 22, 80, 443, 3389)',
      'Check if scan pattern matches known APT tooling (Nmap, Masscan signatures)',
      'Review recent auth logs on targeted hosts for follow-on access attempts',
      'Notify Tier-2 if scanning volume exceeds 500 packets/sec',
    ],
    iocs: ['Unusual SYN scan patterns', 'High port entropy from single IP', 'ICMP sweeps'],
  },
  'APT-Lateral-Movement': {
    title: 'Lateral Movement Detected',
    priority: 'HIGH',
    steps: [
      '⚡ IMMEDIATE: Isolate affected subnet segment — block east-west traffic',
      'Identify all hosts that communicated with source IP in last 2 hours',
      'Check for Pass-the-Hash or Pass-the-Ticket indicators on SMB/Kerberos',
      'Pull process lists from affected hosts via EDR (look for psexec, wmic, mimikatz)',
      'Force password reset on any accounts that authenticated from source IP',
      'Notify Tier-2 and escalate to IR team if >3 hosts compromised',
    ],
    iocs: ['SMB port 445 traffic spike', 'WMI remote execution', 'RDP from unexpected host'],
  },
  'APT-Persistence': {
    title: 'Persistence Mechanism Detected',
    priority: 'HIGH',
    steps: [
      'Identify and quarantine affected host — do NOT power off (preserve memory artifacts)',
      'Pull scheduled tasks, registry run keys, service installations from host',
      'Search for newly created accounts or backdoored existing accounts',
      'Check startup folders and WMI subscriptions for persistence artifacts',
      'Capture full memory dump before remediation for forensic analysis',
      'Rebuild host from golden image after evidence collection',
    ],
    iocs: ['New scheduled tasks', 'Modified registry RunKeys', 'Unexpected new service'],
  },
  'APT-Privilege-Escalation': {
    title: 'Privilege Escalation Detected',
    priority: 'CRITICAL',
    steps: [
      '🚨 CRITICAL: Assume domain compromise — initiate full IR protocol',
      'Force-expire all Kerberos tickets (krbtgt password reset ×2)',
      'Disable compromised account immediately — do not delete (preserve audit trail)',
      'Check for Golden/Silver ticket creation in Domain Controller logs',
      'Audit all admin group memberships for unexpected additions in last 24h',
      'Notify CISO — potential full domain compromise in progress',
    ],
    iocs: ['SYSTEM-level process from user context', 'Kerberoasting patterns', 'SeDebugPrivilege abuse'],
  },
  'APT-Defense-Evasion': {
    title: 'Defense Evasion Detected',
    priority: 'HIGH',
    steps: [
      'Check for log clearing events (Event ID 1102, 104) on affected hosts',
      'Verify integrity of EDR/AV agent — check for tampering or disabled protection',
      'Look for timestomping artifacts on recently accessed files',
      'Search for LOLBAS (Living-off-Land Binary) usage: certutil, regsvr32, mshta',
      'Enable process-level logging (Sysmon Event ID 1, 8) if not already active',
      'Correlate with other incident stages — evasion usually follows initial access',
    ],
    iocs: ['AV/EDR service stopped', 'Audit log cleared', 'Suspicious certutil/powershell usage'],
  },
  'APT-C2-Communication': {
    title: 'C2 Communication Detected',
    priority: 'CRITICAL',
    steps: [
      '🚨 CRITICAL: Block external C2 IP/domain at perimeter AND DNS immediately',
      'Capture full packet capture on affected host network interface for 10 min',
      'Check for DNS-over-HTTPS, domain fronting, or Tor usage as C2 channels',
      'Identify beaconing interval — check for regular call-home patterns',
      'Search for implant binary on disk (hash all new processes vs known-good baseline)',
      'Sinkhole the C2 domain if possible to observe full infected host scope',
    ],
    iocs: ['Regular interval outbound connections', 'Uncommon port usage', 'Base64 in HTTP headers'],
  },
  'APT-Data-Exfiltration': {
    title: 'Data Exfiltration Detected',
    priority: 'CRITICAL',
    steps: [
      '🚨 CRITICAL: Block all outbound traffic from source IP immediately',
      'Identify what data was accessed — pull DLP and file access logs for last 4h',
      'Check cloud storage uploads (OneDrive, Dropbox, GDrive API traffic)',
      'Quantify volume: how many MB/GB left the network? Report to legal/compliance',
      'Preserve all network logs — this is likely a data breach notification event',
      'Notify DPO, Legal, and CISO — breach notification laws may apply (GDPR/CCPA)',
    ],
    iocs: ['Large outbound data transfer', 'Zip/archive creation before transfer', 'HTTPS to new domains'],
  },
  'APT-Living-Off-The-Land': {
    title: 'Living-Off-The-Land Activity Detected',
    priority: 'HIGH',
    steps: [
      'Identify which native binary is being abused (powershell, wscript, certutil, etc.)',
      'Enable PowerShell script block logging if not active (Event ID 4104)',
      'Check for encoded/obfuscated command execution in process args',
      'Review parent process chain — LOLBas often spawned from Office or browser',
      'Block unnecessary binaries via AppLocker/WDAC policy if not already restricted',
      'Search for same technique across all endpoints using EDR threat hunting',
    ],
    iocs: ['PowerShell encoded commands', 'Certutil downloading files', 'Regsvr32 executing scripts'],
  },
};

const PRIORITY_COLOR = { CRITICAL: '#ff0040', HIGH: '#ff3b4e', MEDIUM: '#f59e0b', LOW: '#10b981' };

export default function PlaybookPanel({ attackTypes = [], severity, incidentId, onResolved }) {
  const [expandedType, setExpandedType] = useState(null);
  const [checkedSteps, setCheckedSteps] = useState({});
  const saveTimer = useRef(null);
  useEffect(() => {
    let cancelled = false; setCheckedSteps({});
    if (!incidentId) return;
    (async () => {
      try {
        const res = await apiFetch('/incidents/acks');
        if (!res.ok) return;
        const data = await res.json();
        const rec = (data.acks || {})[incidentId];
        if (!cancelled) setCheckedSteps((rec && rec.playbook_state) ? rec.playbook_state : {});
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, [incidentId]);
  const persist = useCallback((nextState) => {
    if (!incidentId) return;
    const cnt = Object.values(nextState).filter(Boolean).length;
    const total = attackTypes.filter(t => PLAYBOOKS[t]).reduce((n,t)=>n+PLAYBOOKS[t].steps.length,0);
    const allDone = total > 0 && cnt >= total;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await apiFetch('/incidents/' + incidentId + '/playbook', { method:'PUT', body: JSON.stringify({ state: nextState, all_done: allDone }) });
        if (r.ok) { const d = await r.json(); if (d.auto_resolved && typeof onResolved==='function') onResolved(); }
      } catch (e) {}
    }, 400);
  }, [incidentId, attackTypes, onResolved]);

  // Show playbooks for all attack types in this incident
  const applicablePlaybooks = attackTypes
    .filter(t => PLAYBOOKS[t])
    .map(t => ({ type: t, ...PLAYBOOKS[t] }));

  if (applicablePlaybooks.length === 0) return null;

  const toggleStep = (type, idx) => {
    const key = `${type}-${idx}`;
    setCheckedSteps(prev => { const next = { ...prev, [key]: !prev[key] }; persist(next); return next; });
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 10, color: '#4b5563', fontFamily: 'JetBrains Mono, monospace', marginBottom: 8 }}>
        RESPONSE PLAYBOOKS ({applicablePlaybooks.length})
      </div>

      {applicablePlaybooks.map(pb => {
        const col      = PRIORITY_COLOR[pb.priority] || '#6b7280';
        const isOpen   = expandedType === pb.type;
        const completed = pb.steps.filter((_, i) => checkedSteps[`${pb.type}-${i}`]).length;

        return (
          <div key={pb.type} style={{ background: '#0d1018', border: `1px solid ${col}33`, borderRadius: 6, marginBottom: 8, overflow: 'hidden' }}>
            {/* Header */}
            <div onClick={() => setExpandedType(isOpen ? null : pb.type)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', cursor: 'pointer', background: isOpen ? `${col}0a` : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 9, ...mono, fontWeight: 700, color: col, background: `${col}18`, border: `1px solid ${col}44`, padding: '1px 6px', borderRadius: 3 }}>{pb.priority}</span>
                <span style={{ fontSize: 11, color: '#e8eaf0', ...mono }}>{pb.title}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {completed > 0 && (
                  <span style={{ fontSize: 10, color: '#10b981', ...mono }}>{completed}/{pb.steps.length} done</span>
                )}
                <span style={{ fontSize: 12, color: '#6b7280' }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {isOpen && (
              <div style={{ padding: '0 12px 12px' }}>
                {/* Steps */}
                <div style={{ marginBottom: 10 }}>
                  {pb.steps.map((step, i) => {
                    const done = checkedSteps[`${pb.type}-${i}`];
                    return (
                      <div key={i} onClick={() => toggleStep(pb.type, i)}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', borderBottom: '1px solid #ffffff08', cursor: 'pointer' }}>
                        <div style={{ width: 14, height: 14, borderRadius: 3, border: `1px solid ${done ? '#10b981' : '#ffffff22'}`, background: done ? '#10b98122' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                          {done && <span style={{ fontSize: 9, color: '#10b981' }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 11, ...mono, color: done ? '#4b5563' : '#9ca3af', textDecoration: done ? 'line-through' : 'none', lineHeight: 1.4 }}>{step}</span>
                      </div>
                    );
                  })}
                </div>

                {/* IOCs */}
                <div>
                  <div style={{ fontSize: 9, color: '#4b5563', ...mono, marginBottom: 5 }}>INDICATORS OF COMPROMISE</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {pb.iocs.map((ioc, i) => (
                      <span key={i} style={{ fontSize: 9, ...mono, color: '#f59e0b', background: '#f59e0b0a', border: '1px solid #f59e0b22', padding: '2px 6px', borderRadius: 3 }}>{ioc}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
